import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { blogPosts, getLocalizedField, getLocalizedTags, getPostModifiedDate } from "@/data/blogPosts";
import { getLocalizedCaseStudyBySlug } from "@/data/caseStudies";
import { getOgImageForRoute } from "@/data/ogImages";
import { INDEXABLE_ROUTE_PATHS } from "@/lib/routeInventory";
import { usePageLocale } from "@/components/seo/usePageLocale";
import { normalizeSeoLanguage, type SeoLanguage } from "@/lib/seoMetadata";
import { SITE_ORIGIN } from "@/lib/urlNormalise";

// The site's origin has one declaration, `urlNormalise.ts`. This alias keeps
// the composition below readable; it is a name for that constant, not a second
// copy of it. CWS-48 left this literal here deliberately and CWS-67 collected
// it. SYSTEM_DESIGN.md 11.3.
const BASE_URL = SITE_ORIGIN;
const ORG_NAME = "Shelpuk AI Technology Consulting";
const AUTHOR_PROFILE_URL = "https://www.linkedin.com/in/sergiishelpuk/";

const ORGANIZATION_DESCRIPTIONS: Record<SeoLanguage, string> = {
  en:
    "Shelpuk AI Technology Consulting is a European AI consulting and engineering firm that designs, builds, and deploys production-grade AI systems. We specialize in Agentic AI, Computer Vision, Bayesian Intelligence, RAG systems, LLM fine-tuning, and MLOps for enterprises in defence, pharma, banking, and enterprise sectors.",
  de:
    "Shelpuk AI Technology Consulting ist ein europäisches Beratungs- und Engineering-Unternehmen für KI, das produktionsreife KI-Systeme konzipiert, entwickelt und bereitstellt. Unsere Schwerpunkte sind Agentic AI, Computer Vision, Bayesian Intelligence, RAG-Systeme, LLM-Fine-Tuning und MLOps für Unternehmen aus Verteidigung, Pharma, Banking und Enterprise.",
  fr:
    "Shelpuk AI Technology Consulting est un cabinet européen de conseil et d’ingénierie en IA qui conçoit, développe et déploie des systèmes d’IA prêts pour la production. Nous sommes spécialisés en Agentic AI, vision par ordinateur, intelligence bayésienne, systèmes RAG, fine-tuning de LLM et MLOps pour les secteurs défense, pharma, banque et entreprise.",
  it:
    "Shelpuk AI Technology Consulting è una società europea di consulenza e ingegneria AI che progetta, sviluppa e distribuisce sistemi di IA pronti per la produzione. Siamo specializzati in Agentic AI, computer vision, intelligenza bayesiana, sistemi RAG, fine-tuning di LLM e MLOps per i settori difesa, pharma, banking ed enterprise.",
  es:
    "Shelpuk AI Technology Consulting es una firma europea de consultoría e ingeniería de IA que diseña, desarrolla y despliega sistemas de IA listos para producción. Estamos especializados en Agentic AI, visión por computador, inteligencia bayesiana, sistemas RAG, fine-tuning de LLM y MLOps para los sectores de defensa, pharma, banca y enterprise.",
};

const OFFER_CATALOG_COPY: Record<
  SeoLanguage,
  {
    name: string;
    discoveryName: string;
    discoveryDescription: string;
    fixedPriceName: string;
    fixedPriceDescription: string;
    teamsName: string;
    teamsDescription: string;
    enablementName: string;
    enablementDescription: string;
  }
> = {
  en: {
    name: "AI Consulting & Engineering Services",
    discoveryName: "AI Opportunity Discovery",
    discoveryDescription:
      "Strategic consulting to identify high-ROI AI use cases using the Moat vs Enhancer framework and Buy vs Build decision support.",
    fixedPriceName: "Fixed-Price MVP Delivery",
    fixedPriceDescription:
      "Fixed scope, fixed price AI product development with deployment on EU cloud, on-premises, AWS, Azure, or GCP.",
    teamsName: "AI-Native Engineering Teams",
    teamsDescription:
      "Elite engineering teams with 2× velocity using AI-first workflows including Claude Code, Cursor, Codex, and TDD.",
    enablementName: "Engineering Enablement",
    enablementDescription:
      "Training programs to help engineering teams adopt AI-assisted development tools and workflows.",
  },
  de: {
    name: "KI-Beratung und Engineering-Leistungen",
    discoveryName: "KI-Potenzialanalyse",
    discoveryDescription:
      "Strategische Beratung zur Identifikation von KI-Use-Cases mit hohem ROI auf Basis des Moat-vs.-Enhancer-Frameworks und Buy-vs.-Build-Entscheidungshilfen.",
    fixedPriceName: "Festpreis-MVP-Delivery",
    fixedPriceDescription:
      "KI-Produktentwicklung mit festem Umfang und Festpreis, inklusive Deployment in EU-Clouds, On-Premises, AWS, Azure oder GCP.",
    teamsName: "KI-native Engineering-Teams",
    teamsDescription:
      "Erfahrene Engineering-Teams mit bis zu 2× höherer Geschwindigkeit durch AI-first-Workflows mit Claude Code, Cursor, Codex und TDD.",
    enablementName: "Engineering Enablement",
    enablementDescription:
      "Trainingsprogramme, mit denen Engineering-Teams KI-gestützte Entwicklungstools und Workflows wirksam einführen.",
  },
  fr: {
    name: "Services de conseil et d’ingénierie IA",
    discoveryName: "Analyse des opportunités IA",
    discoveryDescription:
      "Conseil stratégique pour identifier des cas d’usage IA à fort ROI à l’aide du cadre Moat vs Enhancer et de l’aide à la décision Buy vs Build.",
    fixedPriceName: "Livraison de MVP à prix fixe",
    fixedPriceDescription:
      "Développement de produits IA à périmètre et prix fixes, avec déploiement sur cloud européen, on-premises, AWS, Azure ou GCP.",
    teamsName: "Équipes d’ingénierie IA-natives",
    teamsDescription:
      "Des équipes d’ingénierie d’élite avec jusqu’à 2× plus de vitesse grâce à des workflows AI-first intégrant Claude Code, Cursor, Codex et le TDD.",
    enablementName: "Engineering Enablement",
    enablementDescription:
      "Programmes de formation pour aider les équipes d’ingénierie à adopter efficacement les outils et workflows de développement assistés par l’IA.",
  },
  it: {
    name: "Servizi di consulenza e ingegneria AI",
    discoveryName: "AI Opportunity Discovery",
    discoveryDescription:
      "Consulenza strategica per identificare casi d’uso AI ad alto ROI usando il framework Moat vs Enhancer e il supporto decisionale Buy vs Build.",
    fixedPriceName: "Consegna MVP a prezzo fisso",
    fixedPriceDescription:
      "Sviluppo di prodotti AI a perimetro e prezzo fissi, con deployment su cloud UE, on-premises, AWS, Azure o GCP.",
    teamsName: "Team di ingegneria AI-native",
    teamsDescription:
      "Team di ingegneria d’élite con fino a 2× più velocità grazie a workflow AI-first con Claude Code, Cursor, Codex e TDD.",
    enablementName: "Engineering Enablement",
    enablementDescription:
      "Programmi di formazione per aiutare i team di ingegneria ad adottare strumenti e workflow di sviluppo assistiti dall’AI.",
  },
  es: {
    name: "Servicios de consultoría e ingeniería de IA",
    discoveryName: "AI Opportunity Discovery",
    discoveryDescription:
      "Consultoría estratégica para identificar casos de uso de IA con alto ROI utilizando el marco Moat vs Enhancer y apoyo en decisiones Buy vs Build.",
    fixedPriceName: "Desarrollo de MVP de IA a precio fijo",
    fixedPriceDescription:
      "Desarrollo de productos de IA con alcance y precio fijos, con despliegue en nube de la UE, on-premises, AWS, Azure o GCP.",
    teamsName: "Equipos de ingeniería AI-native",
    teamsDescription:
      "Equipos de ingeniería de élite con hasta 2× más velocidad gracias a flujos AI-first con Claude Code, Cursor, Codex y TDD.",
    enablementName: "Engineering Enablement",
    enablementDescription:
      "Programas de formación para ayudar a los equipos de ingeniería a adoptar herramientas y flujos de desarrollo asistidos por IA.",
  },
};

function getLocalizedBlogTags(slug: string, lang: SeoLanguage) {
  const post = blogPosts.find((entry) => entry.slug === slug);

  if (!post) {
    return [];
  }
  return getLocalizedTags(post, lang);
}

export function buildOrganizationJsonLdData(lang: string) {
  const safeLang = normalizeSeoLanguage(lang);
  const offerCopy = OFFER_CATALOG_COPY[safeLang];

  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: ORG_NAME,
    alternateName: ["Shelpuk & Company", "Shelpuk AI", "Shelpuk Consulting"],
    url: BASE_URL,
    logo: `${BASE_URL}/logo.png`,
    description: ORGANIZATION_DESCRIPTIONS[safeLang],
    foundingDate: "2022",
    foundingLocation: {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Warsaw",
        addressCountry: "Poland",
      },
    },
    areaServed: ["EU", "Europe", "World"],
    knowsAbout: [
      "Artificial Intelligence",
      "Machine Learning",
      "Computer Vision",
      "Bayesian Networks",
      "Agentic AI",
      "RAG Systems",
      "Retrieval-Augmented Generation",
      "LLM Fine-Tuning",
      "Large Language Models",
      "MLOps",
      "AI Consulting",
      "AI Strategy",
      "Sovereign AI",
      "Natural Language Processing",
      "Deep Learning",
      "PyTorch",
      "LangChain",
      "OpenAI",
      "Claude",
      "Llama",
      "Mistral",
    ],
    sameAs: [
      "https://www.linkedin.com/company/shelpuk-ai-technology-consulting",
      "https://github.com/Shelpuk-AI-Technology-Consulting",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "sales",
      url: "https://calendly.com/shelpuk-ai",
    },
    founder: {
      "@type": "Person",
      name: "Sergii Shelpuk",
      jobTitle: "CEO",
      url: "https://www.linkedin.com/in/sergiishelpuk/",
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: offerCopy.name,
      itemListElement: [
        {
          "@type": "OfferCatalog",
          name: offerCopy.discoveryName,
          description: offerCopy.discoveryDescription,
          url: `${BASE_URL}/${safeLang}/services/consulting`,
        },
        {
          "@type": "OfferCatalog",
          name: offerCopy.fixedPriceName,
          description: offerCopy.fixedPriceDescription,
          url: `${BASE_URL}/${safeLang}/services/fixed-price`,
        },
        {
          "@type": "OfferCatalog",
          name: offerCopy.teamsName,
          description: offerCopy.teamsDescription,
          url: `${BASE_URL}/${safeLang}/services/teams`,
        },
        {
          "@type": "OfferCatalog",
          name: offerCopy.enablementName,
          description: offerCopy.enablementDescription,
          url: `${BASE_URL}/${safeLang}/services/enablement`,
        },
      ],
    },
  };
}

export function OrganizationJsonLd() {
  const organizationData = buildOrganizationJsonLdData(usePageLocale());

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationData) }}
    />
  );
}

export function buildWebsiteJsonLdData(lang: string) {
  const safeLang = normalizeSeoLanguage(lang);

  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: ORG_NAME,
    url: BASE_URL,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${BASE_URL}/${safeLang}/blog?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

export function WebSiteJsonLd() {
  const data = buildWebsiteJsonLdData(usePageLocale());

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

export function buildBlogPostingJsonLdData(slug: string, lang: string) {
  const safeLang = normalizeSeoLanguage(lang);
  const post = blogPosts.find((entry) => entry.slug === slug);

  if (!post) {
    return null;
  }

  // Through the shared resolver rather than indexing the hero map directly, so
  // the JSON-LD image and the page's `og:image` cannot name different files.
  // Identical output today -- no blog route has route art, so the resolver
  // falls through to the same hero-map lookup this line used to make.
  const imagePath = getOgImageForRoute(`blog/${post.slug}`, safeLang);

  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: getLocalizedField(post, "title", safeLang),
    description: getLocalizedField(post, "summary", safeLang),
    datePublished: post.date,
    // The post's real modification date, shared with the sitemap's `lastmod`
    // through the same resolver — never hard-set to the publication date
    // again. SEO.md B15; SYSTEM_DESIGN.md 13a.1.
    dateModified: getPostModifiedDate(post),
    image: imagePath ? `${BASE_URL}${imagePath}` : `${BASE_URL}/og-image-en.png`,
    author: {
      "@type": "Person",
      name: post.author,
      jobTitle: post.authorRole,
      url: AUTHOR_PROFILE_URL,
      sameAs: [AUTHOR_PROFILE_URL],
    },
    publisher: {
      "@type": "Organization",
      name: ORG_NAME,
      url: BASE_URL,
      logo: {
        "@type": "ImageObject",
        url: `${BASE_URL}/logo.png`,
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${BASE_URL}/${safeLang}/blog/${slug}`,
    },
    url: `${BASE_URL}/${safeLang}/blog/${slug}`,
    inLanguage: safeLang,
    keywords: getLocalizedBlogTags(slug, safeLang).join(", "),
    isAccessibleForFree: true,
  };
}

export function BlogPostingJsonLd({ slug }: { slug: string }) {
  const data = buildBlogPostingJsonLdData(slug, usePageLocale());

  if (!data) {
    return null;
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

const ROUTE_LABELS: Record<string, Record<SeoLanguage, string>> = {
  blog: { en: "Blog", de: "Blog", fr: "Blog", it: "Blog", es: "Blog" },
  insights: { en: "Insights", de: "Einblicke", fr: "Analyses & rapports", it: "Approfondimenti e report", es: "Análisis e informes" },
  "case-studies": { en: "Case Studies", de: "Fallstudien", fr: "Études de cas", it: "Casi di studio", es: "Casos de estudio" },
  services: { en: "Services", de: "Leistungen", fr: "Services", it: "Servizi", es: "Servicios" },
  "open-source": { en: "Open Source", de: "Open Source", fr: "Open Source", it: "Open Source", es: "Código abierto" },
  industries: { en: "Industries", de: "Branchen", fr: "Industries", it: "Settori", es: "Industrias" },
  "sovereign-ai": { en: "Sovereign AI", de: "Souveräne KI", fr: "IA souveraine", it: "AI sovrana", es: "IA soberana" },
  terms: { en: "Terms", de: "AGB", fr: "Conditions", it: "Termini", es: "Términos" },
  privacy: { en: "Privacy", de: "Datenschutz", fr: "Confidentialité", it: "Privacy", es: "Privacidad" },
  consulting: { en: "AI Discovery", de: "KI-Potenzialanalyse", fr: "Analyse des opportunités IA", it: "Scoperta di opportunità AI", es: "Descubrimiento de oportunidades de IA" },
  "fixed-price": { en: "Fixed-Price Delivery", de: "Festpreislieferung", fr: "Livraison à prix fixe", it: "Consegna a prezzo fisso", es: "Entrega a precio fijo" },
  teams: { en: "Engineering Teams", de: "Engineering-Teams", fr: "Équipes d'ingénierie", it: "Team di ingegneria", es: "Equipos de ingeniería" },
  enablement: { en: "Engineering Enablement", de: "KI-Engineering-Befähigung", fr: "Formation IA", it: "Abilitazione all'ingegneria AI", es: "Formación en ingeniería con IA" },
  "kindly-web-search": { en: "Kindly Web Search", de: "Kindly Web Search", fr: "Kindly Web Search", it: "Kindly Web Search", es: "Kindly Web Search" },
  "lad-code-review": { en: "Lad Code Review", de: "Lad Code Review", fr: "Lad Code Review", it: "Lad Code Review", es: "Lad Code Review" },
  tdd: { en: "TDD Skill", de: "TDD Skill", fr: "TDD Skill", it: "TDD Skill", es: "TDD Skill" },
  defence: { en: "Defence", de: "Verteidigung", fr: "Défense", it: "Difesa", es: "Defensa" },
  pharma: { en: "Pharma", de: "Pharma", fr: "Pharma", it: "Pharma", es: "Pharma" },
  finance: { en: "Finance", de: "Finanzen", fr: "Finance", it: "Finanza", es: "Finanzas" },
  enterprise: { en: "Enterprise", de: "Enterprise", fr: "Entreprise", it: "AI per le imprese", es: "IA para empresa" },
};

/**
 * Every locale-less route path the site intends to be indexed, as a lookup set.
 *
 * Mirrors the `INDEXED_ROUTE_PATH_SET` idiom in `src/lib/faqResolver.ts`. Only
 * the path arrays are imported — never `INDEXABLE_URLS`, which is 251 objects
 * and must not reach a component (`src/lib/routeInventory.ts`).
 */
const INDEXABLE_ROUTE_PATH_SET = new Set<string>(INDEXABLE_ROUTE_PATHS);

export function getHomeBreadcrumbLabel(lang: string) {
  const safeLang = normalizeSeoLanguage(lang);

  if (safeLang === "de") {
    return "Startseite";
  }

  if (safeLang === "fr") {
    return "Accueil";
  }

  if (safeLang === "it") {
    return "Home";
  }

  if (safeLang === "es") {
    return "Inicio";
  }

  return "Home";
}

export function getBreadcrumbLabel(segment: string, lang: string) {
  const safeLang = normalizeSeoLanguage(lang);
  return ROUTE_LABELS[segment]?.[safeLang];
}

/**
 * Build the `BreadcrumbList` block for one route.
 *
 * Emits at most one `ListItem` per URL path segment, after a leading item for
 * the locale home page. Blog-post and case-study leaf segments resolve to their
 * localized title; every other segment falls back to `ROUTE_LABELS` and then to
 * a title-cased spelling of the slug.
 *
 * **An intermediate rung is emitted only when it names a page that exists** —
 * invariant `S21b`. `src/App.tsx` declares only leaf routes, so mirroring the
 * URL structure advertised `/{lang}/services`, `/{lang}/industries` and
 * `/{lang}/open-source`, none of which resolve (`SEO.md` `B17`, CWS-103); Google
 * likewise recommends a trail that follows "a typical user path to a page,
 * instead of mirroring the URL structure", and the site's own header renders
 * those three as menu labels rather than links. The membership test is against
 * {@link INDEXABLE_ROUTE_PATH_SET} rather than a list of the three names, so a
 * hub that later becomes a real route reappears in the trail with no edit here.
 *
 * The **final** rung is exempt: a breadcrumb has to terminate at the page it
 * describes, and for every indexable route that URL is in the inventory anyway.
 * Trails for paths that are not routes at all — reachable because the
 * `/:lang/*` catch-all renders inside `LanguageRouter` — therefore still close
 * on their own URL, and still carry the two `ListItem`s Google requires. The
 * soft 404 behind them is `SEO.md` `B2`'s to remove, not this function's.
 *
 * `position` is assigned from the array index, so the trail is always the
 * integers `1..n` — the ordering Google requires, and invariant `S21a`.
 *
 * @param pathname - The locale-prefixed URL path, for example
 *     ``/de/industries/pharma``. Leading and repeated slashes are ignored.
 * @param lang - The page locale; normalised through
 *     :func:`normalizeSeoLanguage`, so an unsupported value falls back to `en`.
 * @returns The `BreadcrumbList` object, or ``null`` for a locale home page,
 *     which has no trail above it.
 */
export function buildBreadcrumbJsonLdData(pathname: string, lang: string) {
  const safeLang = normalizeSeoLanguage(lang);

  // The first segment is the locale prefix; the trail describes what follows it.
  const pathParts = pathname.split("/").filter(Boolean);
  const segments = pathParts.slice(1);
  if (segments.length === 0) return null;

  const items: { name: string; url: string }[] = [
    { name: getHomeBreadcrumbLabel(safeLang), url: `${BASE_URL}/${safeLang}` },
  ];

  let currentPath = `/${safeLang}`;
  let routePath = "";
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    currentPath += `/${seg}`;

    // Locale-less and cumulative, because that is the shape `INDEXABLE_ROUTE_PATHS` holds.
    routePath = routePath === "" ? seg : `${routePath}/${seg}`;

    // Above every push site, not only the fallback below: the case-study-miss
    // branch also fires on an intermediate segment.
    if (i < segments.length - 1 && !INDEXABLE_ROUTE_PATH_SET.has(routePath)) {
      continue;
    }

    if (i > 0 && segments[i - 1] === "blog") {
      const post = blogPosts.find((p) => p.slug === seg);
      if (post) {
        items.push({
          name: getLocalizedField(post, "title", safeLang),
          url: `${BASE_URL}${currentPath}`,
        });
        continue;
      }
    }

    if (i > 0 && segments[i - 1] === "case-studies") {
      const caseStudy = getLocalizedCaseStudyBySlug(seg, safeLang);
      if (caseStudy) {
        items.push({
          name: caseStudy.title,
          url: `${BASE_URL}${currentPath}`,
        });
        continue;
      }

      items.push({
        name: seg.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" "),
        url: `${BASE_URL}${currentPath}`,
      });
      continue;
    }

    const label = getBreadcrumbLabel(seg, safeLang);
    if (label) {
      items.push({ name: label, url: `${BASE_URL}${currentPath}` });
    } else {
      items.push({
        name: seg.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" "),
        url: `${BASE_URL}${currentPath}`,
      });
    }
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function BreadcrumbJsonLd() {
  const location = useLocation();
  const data = buildBreadcrumbJsonLdData(location.pathname, usePageLocale());

  if (!data) {
    return null;
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

/**
 * Build the blog index `CollectionPage` block.
 *
 * Lists **every** post, newest first — the list and the order `Blog.tsx`
 * renders in its default state, which invariant `I14` binds this block to.
 * Until CWS-45 it listed `blogPosts.slice(0, 20)` in declaration order, which
 * dropped three posts newer than some it kept (`SEO.md` `B18`). The comparator
 * is deliberately the one `Blog.tsx` uses, and `jsonLdDomContract.test.tsx`
 * compares the two lists element by element, so they cannot drift apart
 * silently.
 *
 * @param heading - The page's own heading in this locale — the component passes
 *     ``t("blog.title", { lng })`` with the URL locale, so the name is in the
 *     same language as the post titles listed beside it. Not ``"Blog"``: that is the filing label, which stays
 *     on `ROUTE_LABELS.blog` and `meta.pages.blog.title` by CWS-104's decision.
 * @param lang - The page locale; normalised through
 *     :func:`normalizeSeoLanguage`.
 * @returns The `CollectionPage` object for the blog index.
 */
export function buildBlogListJsonLdData(heading: string, lang: string) {
  const safeLang = normalizeSeoLanguage(lang);
  const newestFirst = [...blogPosts].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${heading} — ${ORG_NAME}`,
    description:
      safeLang === "de"
        ? "Technische Artikel und strategische Einblicke zu KI, Machine Learning, LLM-Fine-Tuning und Wettbewerbsvorteilen durch KI."
        : safeLang === "fr"
          ? "Articles techniques et analyses stratégiques sur l'IA, le machine learning, le fine-tuning des LLM et la création d'un avantage concurrentiel grâce à l'IA."
          : safeLang === "it"
            ? "Articoli tecnici e approfondimenti strategici su AI, machine learning, fine-tuning di LLM e costruzione di vantaggio competitivo con l’AI."
            : safeLang === "es"
              ? "Artículos técnicos e insights estratégicos sobre IA, machine learning, fine-tuning de LLM y construcción de ventaja competitiva con IA."
          : "Technical articles and strategic insights on AI, machine learning, LLM fine-tuning, and building competitive advantage with AI.",
    url: `${BASE_URL}/${safeLang}/blog`,
    inLanguage: safeLang,
    publisher: { "@type": "Organization", name: ORG_NAME, url: BASE_URL },
    mainEntity: {
      "@type": "ItemList",
      itemListElement: newestFirst.map((post, idx) => ({
        "@type": "ListItem",
        position: idx + 1,
        url: `${BASE_URL}/${safeLang}/blog/${post.slug}`,
        name: getLocalizedField(post, "title", safeLang),
      })),
    },
  };
}

export function BlogListJsonLd() {
  const { t } = useTranslation();
  const lang = usePageLocale();
  // `lng` pins the heading to the URL's locale, like the post titles this
  // builder lists, so one block cannot name itself in a different language from
  // its own items. Enforced by the locale case in `jsonLdDomContract.test.tsx`.
  const data = buildBlogListJsonLdData(t("blog.title", { lng: lang }), lang);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

/**
 * Build the case-study index `CollectionPage` block.
 *
 * The caller supplies the items because the index page has already localized
 * them; this builder only wraps them and adds no list of its own.
 *
 * `CaseStudies.tsx` passes the **full** set while rendering `filtered`, which
 * narrows when a visitor picks a tag. The two therefore agree in the state a
 * crawler sees — `activeTag` starts `null` — and diverge only after an
 * interaction no crawler performs. Accurate as indexed, then; not an invariant.
 *
 * @param items - The case studies to list, in display order, each with its
 *     canonical English `slug` and its already-localized `title`.
 * @param heading - The page's badge in this locale — the component passes
 *     ``t("caseStudies.badge", { lng })`` with the URL locale. Until CWS-45 the name was a per-locale
 *     ternary here, and two of its five values (`Casi studio`,
 *     `Casos de estudio`) were strings the page never rendered.
 * @param lang - The page locale; normalised through
 *     :func:`normalizeSeoLanguage`.
 * @returns The `CollectionPage` object for the case-study index.
 */
export function buildCaseStudiesListJsonLdData(
  items: Array<{ slug: string; title: string }>,
  heading: string,
  lang: string,
) {
  const safeLang = normalizeSeoLanguage(lang);

  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${heading} — ${ORG_NAME}`,
    description:
      safeLang === "de"
        ? "Referenzprojekte und Fallstudien von Shelpuk AI Technology Consulting in den Bereichen Pharma, Banking, Verteidigung und Enterprise."
        : safeLang === "fr"
          ? "Projets de référence et études de cas de Shelpuk AI Technology Consulting dans les secteurs pharma, bancaire, défense et entreprise."
          : safeLang === "it"
            ? "Progetti di riferimento e casi studio di Shelpuk AI Technology Consulting nei settori pharma, banking, difesa ed enterprise."
            : safeLang === "es"
              ? "Proyectos de referencia y casos de estudio de Shelpuk AI Technology Consulting en los sectores pharma, banca, defensa y enterprise."
          : "Reference projects and case studies by Shelpuk AI Technology Consulting across pharma, banking, defence, and enterprise sectors.",
    url: `${BASE_URL}/${safeLang}/case-studies`,
    inLanguage: safeLang,
    publisher: { "@type": "Organization", name: ORG_NAME, url: BASE_URL },
    mainEntity: {
      "@type": "ItemList",
      itemListElement: items.map((item, idx) => ({
        "@type": "ListItem",
        position: idx + 1,
        url: `${BASE_URL}/${safeLang}/case-studies/${item.slug}`,
        name: item.title,
      })),
    },
  };
}

export function CaseStudiesListJsonLd({
  items,
}: {
  items: Array<{ slug: string; title: string }>;
}) {
  const { t } = useTranslation();
  const lang = usePageLocale();
  const data = buildCaseStudiesListJsonLdData(
    items,
    t("caseStudies.badge", { lng: lang }),
    lang,
  );

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

/**
 * Build the `FAQPage` block from already-resolved FAQ entries.
 *
 * The entries come from :func:`resolveFaqsForPathname`, the same call that
 * feeds :class:`VisibleFaqSection`, so the markup and the visible accordion are
 * two renderings of one list rather than two lists that must be kept in step.
 * Order is preserved, which is what invariant `S22a` pins. Whether these pairs
 * match the *visible* FAQ content is `S22b`, owned by `E2.8a` and `E4.4`.
 *
 * **Carries `inLanguage`** since CWS-45. `FAQPage` is
 * `Thing > CreativeWork > WebPage > FAQPage` — a document describing this page,
 * so it is stamped exactly as its `CollectionPage` sibling is. Its previous
 * absence was inherited rather than decided (`SEO.md` `B18` item (a)). The
 * house rule that replaced the inherited split is in `SYSTEM_DESIGN.md` §13a.2:
 * a block carries `inLanguage` if and only if it is a **page-scoped**
 * `CreativeWork` — which is why `WebSite`, also a `CreativeWork`, does not.
 *
 * @param faqs - The resolved question/answer pairs, in display order.
 * @param lang - The page locale; normalised through
 *     :func:`normalizeSeoLanguage`.
 * @returns The `FAQPage` object. An empty `faqs` array yields an empty
 *     `mainEntity`. Note that :func:`FAQPageJsonLd` does **not** guard on
 *     length — the guard lives at the call site in `LanguageRouter`, which
 *     renders the block only when the route resolved any FAQs. A new caller must
 *     bring its own guard rather than assume the wrapper has one.
 */
export function buildFaqPageJsonLdData(
  faqs: Array<{ question: string; answer: string }>,
  lang: string,
) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    inLanguage: normalizeSeoLanguage(lang),
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

export function FAQPageJsonLd({ faqs }: { faqs: Array<{ question: string; answer: string }> }) {
  const data = buildFaqPageJsonLdData(faqs, usePageLocale());

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}
